const DEFAULT_FEATURES = {
  agent_create_feature: true,
  analytic: true,
  workflow_feature: false,
  crm_feature: false,
  chatbot_feature: false,
  whatsapp_feature: false,
  inbound_calling_feature: false,
  voice_clone_feature: false,
  products_feature: false,
  knowledge_feature: false,
  add_credit_feature: false,
  background_noise_simulation: true,
  batch_call_concurrency: 10,
  call_outcome: true,
  conversation_metadata: true,
  hang_up_on_silence: false,
  interruption_handling: true,
  primary_did_rotation: false,
  did_limit_rotation: false,
  show_customer_phone: false,
  supervision: false,
  timeliner: false,
  demo_mode: false,
  csv_upload_limit: 1000,
  callback_limit_days: 31,
  total_call_timeout: false,
};

export const DEFAULT_SHORT_CALL_RETRY_SECONDS = 25;
export const DEFAULT_AUTO_DISCONNECT_TIMEOUT_SECONDS = 310;
export const DEFAULT_AUTO_DISCONNECT_MESSAGE = 'कॉल 5 मिनट से ऊपर चलने की वजह से यह कॉल cut की जा रही है';
export const DEFAULT_CALL_RETRY_ENABLED = false;
export const DEFAULT_SHORT_CALL_RETRY_ENABLED = false;
export const DEFAULT_LLM_SKIP_ENABLED = true;
export const DEFAULT_LLM_SKIP_SECONDS = 25;
export const DEFAULT_CALL_RETRY_ATTEMPTS = 2;
// Comma-separated per-attempt delays ("30,60"); a single value ("60") applies
// uniformly to all attempts.
export const DEFAULT_CALL_RETRY_DELAY_MINUTES = '30,60';
export const DEFAULT_USER_AWAY_TIMEOUT_SECONDS = 8;
export const DEFAULT_AWAY_FIRST_MESSAGE = 'Hello? क्या आप सुन रहे हैं?';
export const DEFAULT_AWAY_DISCONNECT_COUNTDOWN_SECONDS = 15;
export const DEFAULT_AWAY_DISCONNECT_MESSAGE = 'जवाब न मिलने पर यह कॉल cut की जा रही है';
// Blind SIP-transfer (call forwarding) — all OFF by default.
export const DEFAULT_TRANSFER_ENABLED = false;
export const DEFAULT_TRANSFER_ON_TIME_LIMIT_ENABLED = false;
export const DEFAULT_TRANSFER_ON_TIME_LIMIT_LABEL = '';
export const DEFAULT_TRANSFER_ON_TIME_LIMIT_MESSAGE = 'कृपया लाइन पर बने रहें, मैं आपको हमारी टीम से जोड़ रही हूँ।';
export const DEFAULT_TRANSFER_AFTER_QUESTIONS_ENABLED = false;
export const DEFAULT_TRANSFER_AFTER_QUESTIONS_COUNT = 3;
export const DEFAULT_TRANSFER_AFTER_QUESTIONS_LABEL = '';
export const DEFAULT_TRANSFER_AFTER_QUESTIONS_MESSAGE = 'मैं आपको हमारे एक एजेंट से जोड़ रही हूँ जो आपकी बेहतर मदद कर सकेंगे।';
// Admin-owned per-agent call toggles — all OFF by default. Core applies them to
// every call for the agent; clients only see them.
export const DEFAULT_NOISE_CANCELLATION_ENABLED = false;
export const DEFAULT_SPEAKER_ISOLATION_ENABLED = false;
export const DEFAULT_NEXGEN_THINKER_ENABLED = false;

// "owner" is an organisation-level client role. Only the platform-level
// "admin" role may enter the /admin workspace.
const ADMIN_ROLES = new Set(['admin']);
const LANGFUSE_ROLES = new Set(['monitor_langfuse', 'monitor_all']);
const TIMELINER_ROLES = new Set(['monitor_timeliner', 'monitor_all']);

function normalizeRole(user) {
  return String(user?.role || '').trim().toLowerCase();
}

function normalizeEmail(user) {
  return String(user?.email || '').trim().toLowerCase();
}

export function isAdminUser(user) {
  return ADMIN_ROLES.has(normalizeRole(user));
}

export function isLangfuseUser(user) {
  return LANGFUSE_ROLES.has(normalizeRole(user)) || normalizeEmail(user) === 'langfuse@gmail.com';
}

export function isTimelinerUser(user) {
  return TIMELINER_ROLES.has(normalizeRole(user)) || normalizeEmail(user) === 'timeliner@gmail.com' || getBooleanSetting(user, 'timeliner', false);
}

export function isClinicalSimUser(user) {
  return String(user?.dashboard_type || '').trim().toLowerCase() === 'clinicalsim';
}

export function isClinicalSimAdmin(user) {
  return isClinicalSimUser(user) && isAdminUser(user);
}

export function getBooleanSetting(user, fieldName, fallback = false) {
  if (!user) return fallback;
  const value = user[fieldName];
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
  }
  return fallback;
}

export function getNumberSetting(user, fieldName, fallback = 0) {
  const candidate = user?.[fieldName];
  const parsed = Number(candidate);
  if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  return fallback;
}

export function getStringSetting(user, fieldName, fallback = '') {
  const value = String(user?.[fieldName] || '').trim();
  return value || fallback;
}

function getRuntimeCallSettings(source = {}) {
  return {
    short_call_retry_seconds: getNumberSetting(source, 'short_call_retry_seconds', DEFAULT_SHORT_CALL_RETRY_SECONDS),
    auto_disconnect_timeout_seconds: getNumberSetting(
      source,
      'auto_disconnect_timeout_seconds',
      DEFAULT_AUTO_DISCONNECT_TIMEOUT_SECONDS
    ),
    auto_disconnect_message: getStringSetting(
      source,
      'auto_disconnect_message',
      DEFAULT_AUTO_DISCONNECT_MESSAGE
    ),
    user_away_timeout_seconds: getNumberSetting(
      source,
      'user_away_timeout_seconds',
      DEFAULT_USER_AWAY_TIMEOUT_SECONDS
    ),
    away_first_message: getStringSetting(source, 'away_first_message', DEFAULT_AWAY_FIRST_MESSAGE),
    away_disconnect_countdown_seconds: getNumberSetting(
      source,
      'away_disconnect_countdown_seconds',
      DEFAULT_AWAY_DISCONNECT_COUNTDOWN_SECONDS
    ),
    away_disconnect_message: getStringSetting(source, 'away_disconnect_message', DEFAULT_AWAY_DISCONNECT_MESSAGE),
    call_retry_enabled: getBooleanSetting(source, 'call_retry_enabled', DEFAULT_CALL_RETRY_ENABLED),
    short_call_retry_enabled: getBooleanSetting(source, 'short_call_retry_enabled', DEFAULT_SHORT_CALL_RETRY_ENABLED),
    llm_skip_enabled: getBooleanSetting(source, 'llm_skip_enabled', DEFAULT_LLM_SKIP_ENABLED),
    llm_skip_seconds: getNumberSetting(source, 'llm_skip_seconds', DEFAULT_LLM_SKIP_SECONDS),
    call_retry_attempts: getNumberSetting(source, 'call_retry_attempts', DEFAULT_CALL_RETRY_ATTEMPTS),
    // String: "60" (uniform) or "30,60" (per-attempt). getStringSetting also
    // coerces legacy numeric values to their string form.
    call_retry_delay_minutes: getStringSetting(source, 'call_retry_delay_minutes', DEFAULT_CALL_RETRY_DELAY_MINUTES),
    // Blind SIP-transfer controls (label→number map + the two auto-triggers).
    transfer_enabled: getBooleanSetting(source, 'transfer_enabled', DEFAULT_TRANSFER_ENABLED),
    transfer_destinations: normalizeTransferDestinations(source?.transfer_destinations),
    transfer_on_time_limit_enabled: getBooleanSetting(
      source,
      'transfer_on_time_limit_enabled',
      DEFAULT_TRANSFER_ON_TIME_LIMIT_ENABLED
    ),
    transfer_on_time_limit_label: getStringSetting(source, 'transfer_on_time_limit_label', DEFAULT_TRANSFER_ON_TIME_LIMIT_LABEL),
    transfer_on_time_limit_message: getStringSetting(
      source,
      'transfer_on_time_limit_message',
      DEFAULT_TRANSFER_ON_TIME_LIMIT_MESSAGE
    ),
    transfer_after_questions_enabled: getBooleanSetting(
      source,
      'transfer_after_questions_enabled',
      DEFAULT_TRANSFER_AFTER_QUESTIONS_ENABLED
    ),
    transfer_after_questions_count: getNumberSetting(
      source,
      'transfer_after_questions_count',
      DEFAULT_TRANSFER_AFTER_QUESTIONS_COUNT
    ),
    transfer_after_questions_label: getStringSetting(
      source,
      'transfer_after_questions_label',
      DEFAULT_TRANSFER_AFTER_QUESTIONS_LABEL
    ),
    transfer_after_questions_message: getStringSetting(
      source,
      'transfer_after_questions_message',
      DEFAULT_TRANSFER_AFTER_QUESTIONS_MESSAGE
    ),
    noise_cancellation: getBooleanSetting(source, 'noise_cancellation', DEFAULT_NOISE_CANCELLATION_ENABLED),
    speaker_isolation: getBooleanSetting(source, 'speaker_isolation', DEFAULT_SPEAKER_ISOLATION_ENABLED),
    thinking_nextgen: getBooleanSetting(source, 'thinking_nextgen', DEFAULT_NEXGEN_THINKER_ENABLED),
  };
}

// Lowercase/trim labels and reduce numbers to digits/plus (00→+), matching the
// core normalizer, so the map echoed to test calls / metadata is clean.
export function normalizeTransferDestinations(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const cleaned = {};
  for (const [label, number] of Object.entries(value)) {
    const key = String(label || '').trim().toLowerCase();
    let num = String(number || '').replace(/[^\d+]/g, '');
    if (num.startsWith('00')) num = `+${num.slice(2)}`;
    if (key && num) cleaned[key] = num;
  }
  return cleaned;
}

export function isDemoMode(user) {
  return getBooleanSetting(user, 'demo_mode', DEFAULT_FEATURES.demo_mode);
}

export function canManageAgents(user) {
  return isAdminUser(user) || getBooleanSetting(user, 'agent_create_feature', DEFAULT_FEATURES.agent_create_feature);
}

export function canAccessAnalytics(user) {
  return (
    isAdminUser(user)
    || isLangfuseUser(user)
    || isTimelinerUser(user)
    || getBooleanSetting(user, 'analytic', DEFAULT_FEATURES.analytic)
  );
}

// Hybrid Dialer supervision console. Opt-in per account and NOT granted by admin role:
// seeing other people's live customer conversations is a deliberate grant, not a
// side effect of being an administrator.
export function canAccessSupervision(user) {
  return isDemoMode(user) || getBooleanSetting(user, 'supervision', DEFAULT_FEATURES.supervision);
}

// Workflow builder (the disposition-graph studio + /home/automation). Off by
// default; granted per account from the TechLadder admin panel (workflow_feature),
// after which the org owner grants members the workflows.* permissions.
export function canAccessWorkflows(user) {
  return getBooleanSetting(user, 'workflow_feature', DEFAULT_FEATURES.workflow_feature);
}

// CRM tab (lead categories, leads, manual calling, workflow batch upload).
// Off by default; granted per account from the TechLadder admin panel
// (crm_feature), same shape as canAccessWorkflows above.
export function canAccessCrm(user) {
  return getBooleanSetting(user, 'crm_feature', DEFAULT_FEATURES.crm_feature);
}

// Chatbot tab. Off by default; granted per account from the TechLadder admin
// panel (chatbot_feature), same shape as canAccessCrm above. The page itself
// is a placeholder until the chatbot feature ships.
export function canAccessChatbot(user) {
  return getBooleanSetting(user, 'chatbot_feature', DEFAULT_FEATURES.chatbot_feature);
}

// Settings > WhatsApp tab (connect numbers, QR/Meta Business pairing). Off by
// default; granted per account from the TechLadder admin panel
// (whatsapp_feature), same shape as canAccessChatbot above.
// Phone Numbers page (/phone-numbers). Off by default; granted per account
// from the TechLadder admin panel (inbound_calling_feature).
export function canAccessInboundCalling(user) {
  return isDemoMode(user) || getBooleanSetting(user, 'inbound_calling_feature', DEFAULT_FEATURES.inbound_calling_feature);
}

export function canAccessWhatsApp(user) {
  return getBooleanSetting(user, 'whatsapp_feature', DEFAULT_FEATURES.whatsapp_feature);
}

// Cloned-voices page (/voices). Off by default; granted per account from the
// TechLadder admin panel (voice_clone_feature), after which the org owner
// grants members the voice.clone permission. The provider behind a clone is
// never disclosed in the UI.
export function canAccessVoiceCloning(user) {
  return getBooleanSetting(user, 'voice_clone_feature', DEFAULT_FEATURES.voice_clone_feature);
}

// Products page (/products). Off by default; granted per account from the
// TechLadder admin panel (products_feature). The API enforces the same switch.
export function canAccessProducts(user) {
  return getBooleanSetting(user, 'products_feature', DEFAULT_FEATURES.products_feature);
}

// Knowledge page and the agent's Knowledge panel. Off by default; granted per account
// from the TechLadder admin panel (knowledge_feature). The API enforces the same switch.
export function canAccessKnowledge(user) {
  return canManageAgents(user) && getBooleanSetting(user, 'knowledge_feature', DEFAULT_FEATURES.knowledge_feature);
}

// Add-credit entry point. Products access is also required because the button
// navigates to the protected Products page.
export function canAddCredits(user) {
  return canAccessProducts(user)
    && getBooleanSetting(user, 'add_credit_feature', DEFAULT_FEATURES.add_credit_feature);
}

export function canViewCallOutcomes(user) {
  return isAdminUser(user) || getBooleanSetting(user, 'call_outcome', DEFAULT_FEATURES.call_outcome);
}

export function canViewConversationMetadata(user) {
  return isAdminUser(user) || getBooleanSetting(user, 'conversation_metadata', DEFAULT_FEATURES.conversation_metadata);
}

export function canViewCustomerPhone(user) {
  return getBooleanSetting(user, 'show_customer_phone', DEFAULT_FEATURES.show_customer_phone);
}

export function getScheduleSettings(user) {
  const interruptionEnabled = getBooleanSetting(user, 'interruption_handling', DEFAULT_FEATURES.interruption_handling);
  return {
    background_noise: getBooleanSetting(user, 'background_noise_simulation', DEFAULT_FEATURES.background_noise_simulation),
    batch_call_concurrency: getNumberSetting(user, 'batch_call_concurrency', DEFAULT_FEATURES.batch_call_concurrency),
    interruption_enabled: interruptionEnabled,
    interruption: false,
    primary_did_rotation: getBooleanSetting(user, 'primary_did_rotation', DEFAULT_FEATURES.primary_did_rotation),
    did_limit_rotation: getBooleanSetting(user, 'did_limit_rotation', DEFAULT_FEATURES.did_limit_rotation),
    csv_upload_limit: getNumberSetting(user, 'csv_upload_limit', DEFAULT_FEATURES.csv_upload_limit),
  };
}

export function getDefaultAuthenticatedRoute(user) {
  if (user?.role === 'viewer') return '/dashboard-access/users';
  if (isClinicalSimUser(user)) return '/cs/library';
  if (isAdminUser(user)) return '/admin/users';
  const isDedicatedTimeliner = TIMELINER_ROLES.has(normalizeRole(user)) || normalizeEmail(user) === 'timeliner@gmail.com';
  if (isLangfuseUser(user) || isDedicatedTimeliner) return '/analytics';
  return '/';
}

export function getFeatureDefaults() {
  return { ...DEFAULT_FEATURES };
}

export function getAgentScheduleSettings(user, agentId, multiAgentConfigId) {
  const overrides = user?.agent_schedule_overrides || {};
  const workflowOverride = multiAgentConfigId ? overrides[`workflow:${multiAgentConfigId}`] : null;
  const agentOverride = agentId ? overrides[agentId] : null;
  const effective = workflowOverride || agentOverride;

  if (effective) {
    return {
      schedule_time_start: String(effective.schedule_time_start || '11:00'),
      schedule_time_end: String(effective.schedule_time_end || '20:00'),
      schedule_timezone: String(effective.schedule_timezone || 'Asia/Kolkata (IST, UTC+5:30)'),
      ...getRuntimeCallSettings(effective),
    };
  }

  return {
    schedule_time_start: '11:00',
    schedule_time_end: '20:00',
    schedule_timezone: 'Asia/Kolkata (IST, UTC+5:30)',
    ...getRuntimeCallSettings(),
  };
}
